/**
 * Route Protection Configuration
 * 
 * Centralized configuration for protected routes and blocking conditions.
 * Add new conditions or protected routes here in one place.
 * 
 * USER TYPES:
 * -----------
 * External Users (affected by route protection):
 *   - Suppliers: External supplier users
 *   - Factories: External factory users
 * 
 * Internal/Admin Users (NOT affected by route protection):
 *   - isRslUser: RSL (Restricted Substances List) Admin user
 *   - isNloUser: NLO (Nike Licensing Operations) user
 *   - isLabUser: Lab user
 */

/**
 * List of protected route paths that require all conditions to be met
 * Add new protected routes here
 */
export const PROTECTED_PATHS = [
  '/trf',
  '/qrf', 
  '/frf',
  '/test-details',
  '/labresult',
  '/download-trf',
  '/download-qrf',
  '/download-frf'
];

/**
 * Check if user is a Supplier or Factory user
 * @param {Object} userDetails - User details from Redux state
 * @returns {boolean}
 */
export const isSupplierOrFactoryUser = (userDetails) => {
  if (!userDetails) return false;
  return userDetails.savedUserType === "Suppliers" || userDetails.savedUserType === "Factories";
};

/**
 * Check if user is an admin/internal type
 * 
 * Admin user types (not blocked by route protection):
 * - isRslUser: RSL (Restricted Substances List) Admin user
 * - isNloUser: NLO (Nike Licensing Operations) user
 * - isLabUser: Lab user
 * 
 * @param {Object} flags - Object containing user type flags
 * @param {boolean} flags.isRslUser - Is RSL (Restricted Substances List) Admin user
 * @param {boolean} flags.isNloUser - Is NLO (Nike Licensing Operations) user
 * @param {boolean} flags.isLabUser - Is Lab user
 * @returns {boolean}
 */
export const isAdminUser = ({ isRslUser, isNloUser, isLabUser }) => {
  return isRslUser || isNloUser || isLabUser;
};

/**
 * ============================================
 * STATUS FIELD CONFIGURATION
 * ============================================
 * Define all status fields here. To add a new condition:
 * 1. Add the field name and its corresponding userDetails property
 * 2. Set isRequired: true if it should block route access
 */
export const STATUS_FIELDS = {
  TRAINING_MATERIAL: {
    name: 'Training Material',
    statusKey: 'TrainingMaterialStatus',    // Property name in userDetails
    isRequired: true                         // Set true to block routes if not completed
  },
  OSH: {
    name: 'OSH (Occupational Safety and Health)',
    statusKey: 'OSHStatus',
    isRequired: false                        // Set true to enable blocking
  },
  LITMOS: {
    name: 'Litmos Training',
    statusKey: 'LitmosStatus',
    isRequired: false                        // Set true to enable blocking
  }
  // Add more status fields here as needed
};

/**
 * ============================================
 * REUSABLE CONDITION CHECKS
 * ============================================
 * Generic functions that work with any status field
 */

/**
 * Check if a specific status is NOT completed
 * @param {Object} userDetails - User details from Redux state
 * @param {string} statusKey - The status property name (e.g., 'TrainingMaterialStatus')
 * @returns {boolean} - Returns true if status is NOT completed
 */
export const isStatusNotCompleted = (userDetails, statusKey) => {
  return userDetails?.[statusKey] !== 'completed';
};

/**
 * Check if a specific status IS completed
 * @param {Object} userDetails - User details from Redux state
 * @param {string} statusKey - The status property name (e.g., 'TrainingMaterialStatus')
 * @returns {boolean} - Returns true if status IS completed
 */
export const isStatusCompleted = (userDetails, statusKey) => {
  return userDetails?.[statusKey] === 'completed';
};

/**
 * Check all required statuses and return which ones are not completed
 * @param {Object} userDetails - User details from Redux state
 * @returns {Array} - Array of incomplete required status names
 */
export const getIncompleteRequiredStatuses = (userDetails) => {
  const incomplete = [];
  
  Object.values(STATUS_FIELDS).forEach(field => {
    if (field.isRequired && isStatusNotCompleted(userDetails, field.statusKey)) {
      incomplete.push(field.name);
    }
  });
  
  return incomplete;
};

/**
 * Check if any required status is not completed
 * @param {Object} userDetails - User details from Redux state
 * @returns {boolean} - Returns true if ANY required status is not completed
 */
export const hasIncompleteRequiredStatus = (userDetails) => {
  return Object.values(STATUS_FIELDS).some(field => 
    field.isRequired && isStatusNotCompleted(userDetails, field.statusKey)
  );
};

/**
 * Main function to check if user should be blocked from accessing protected routes
 * 
 * @param {Object} params
 * @param {Object} params.userDetails - User details from Redux state
 * @param {boolean} params.isRslUser - Is RSL (Restricted Substances List) Admin user
 * @param {boolean} params.isNloUser - Is NLO (Nike Licensing Operations) user  
 * @param {boolean} params.isLabUser - Is Lab user
 * @returns {Object} { shouldBlock: boolean, reason: string | null, incompleteStatuses: Array }
 */
export const shouldBlockRouteAccess = ({ userDetails, isRslUser, isNloUser, isLabUser }) => {
  // Admin users are never blocked
  if (isAdminUser({ isRslUser, isNloUser, isLabUser })) {
    return { shouldBlock: false, reason: null, incompleteStatuses: [] };
  }

  // Only check for Supplier/Factory users
  if (!isSupplierOrFactoryUser(userDetails)) {
    return { shouldBlock: false, reason: null, incompleteStatuses: [] };
  }

  // Check all required statuses from STATUS_FIELDS configuration
  const incompleteStatuses = getIncompleteRequiredStatuses(userDetails);
  
  if (incompleteStatuses.length > 0) {
    return { 
      shouldBlock: true, 
      reason: 'incompleteRequiredStatus', 
      incompleteStatuses 
    };
  }

  return { shouldBlock: false, reason: null, incompleteStatuses: [] };
};

/**
 * Check if a given path is a protected route
 * @param {string} path - The route path to check
 * @returns {boolean}
 */
export const isProtectedRoute = (path) => {
  return PROTECTED_PATHS.includes(path);
};

/**
 * Get the redirect path when user is blocked
 * Can be extended to redirect to different pages based on reason
 * @param {string} reason - The blocking reason
 * @returns {string} - The redirect path
 */
export const getBlockedRedirectPath = (reason) => {
  // Currently all blocks redirect to action-center
  // In future, can customize based on reason:
  // switch (reason) {
  //   case 'trainingMaterialNotCompleted':
  //     return '/action-center';
  //   case 'profileIncomplete':
  //     return '/user';
  //   default:
  //     return '/action-center';
  // }
  return '/action-center';
};

/**
 * Check and redirect if user should be blocked
 * Use this in component's componentDidMount for fallback protection
 * 
 * @param {Object} params
 * @param {Object} params.userDetails - User details from Redux state
 * @param {boolean} params.isRslUser - Is RSL (Restricted Substances List) Admin user
 * @param {boolean} params.isNloUser - Is NLO (Nike Licensing Operations) user
 * @param {boolean} params.isLabUser - Is Lab user
 * @param {Object} params.history - React Router history object
 * @returns {boolean} - Returns true if redirected (caller should return early)
 */
export const checkAndRedirectIfBlocked = ({ userDetails, isRslUser, isNloUser, isLabUser, history }) => {
  const { shouldBlock, reason } = shouldBlockRouteAccess({ 
    userDetails, 
    isRslUser, 
    isNloUser, 
    isLabUser 
  });
  
  if (shouldBlock) {
    const redirectPath = getBlockedRedirectPath(reason);
    history.push(redirectPath);
    return true;
  }
  
  return false;
};
